import { type FC, type ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'

import { FormHelperText, Radio } from 'base-ui'

import { type MarketplacePartnerRegisterPageFormModel } from '../../marketplacePartnerRegisterPageMakeFormState'
import { type makeMarketplacePartnerRegisterPageFormValidations } from '../../marketplacePartnerRegisterPageMakeFormValidations'
import { useMarketplacePartnerRegisterPageContext } from '../marketplacePartnerRegisterPageContext'

type MarketplacePartnerRegisterPageRadioFieldName = Extract<
  keyof MarketplacePartnerRegisterPageFormModel,
  keyof ReturnType<typeof makeMarketplacePartnerRegisterPageFormValidations>
>

type MarketplacePartnerRegisterPageFormRadioFieldProps = {
  fieldName: MarketplacePartnerRegisterPageRadioFieldName
  label: ReactNode
  resetFieldNameWhenUnchecked?: keyof MarketplacePartnerRegisterPageFormModel
}
export const MarketplacePartnerRegisterPageFormRadioField: FC<
  MarketplacePartnerRegisterPageFormRadioFieldProps
> = ({ fieldName, label, resetFieldNameWhenUnchecked }) => {
  const { formState } = useMarketplacePartnerRegisterPageContext()
  formState.use()

  return (
    <span className="flex flex-col gap-4">
      <span className="text-l">{label}</span>
      <fieldset className="flex flex-row gap-5">
        <label className="flex items-center gap-2">
          <Radio
            checked={Boolean(formState.model[fieldName])}
            data-qa-id="sellerForm-radio-retailMediaMarketing"
            onChange={(): void => {
              formState.setValue(true, formState.model, fieldName)
              formState.model.validator.validate(fieldName)
            }}
          />
          <span>
            <FormattedMessage id="marketplacePartnerRegisterPage.basicInformations.respectiveLanguage.option1" />
          </span>
        </label>
        <label className="flex items-center gap-2">
          <Radio
            checked={!formState.model[fieldName]}
            data-qa-id="sellerForm-radio-noRetailMediaMarketing"
            onChange={(): void => {
              formState.setValue(false, formState.model, fieldName)
              formState.model.validator.validate(fieldName)
              if (!resetFieldNameWhenUnchecked) {
                return
              }

              // if there is associated selection field, reset its value when radio value is negative
              formState.setValue(
                '',
                formState.model,
                resetFieldNameWhenUnchecked,
              )
              formState.model.validator.removeErrors(
                resetFieldNameWhenUnchecked,
              )
            }}
          />
          <span>
            <FormattedMessage id="marketplacePartnerRegisterPage.basicInformations.respectiveLanguage.option2" />
          </span>
        </label>
      </fieldset>
      {formState.model.validator.getFirstErrorFor(fieldName) && (
        <FormHelperText
          data-qa-id={`global-MessageForm-${fieldName}-error-msg`}
          error
        >
          {formState.model.validator.getFirstErrorFor(fieldName)}
        </FormHelperText>
      )}
    </span>
  )
}
